import { useEffect, useState } from "react";
import axios from "axios";
import { useLanguage } from "../../i18n/LanguageContext";
import { CITIES, INDUSTRIES, defaultValue } from "../../data/roiData";
import { Reveal } from "../Reveal";
import { CheckCircle2, Send } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const LeadCapture = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", whatsapp: "", city: "Riyadh", industry: "Clinic", missedCalls: 20, apptValue: defaultValue("Riyadh", "Clinic") });
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    setForm((f) => ({ ...f, apptValue: defaultValue(f.city, f.industry) }));
  }, [form.city, form.industry]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/lead`, {
        name: form.name.trim(),
        whatsapp: form.whatsapp.trim(),
        city: form.city,
        industry: form.industry,
        missedCalls: parseInt(form.missedCalls) || 0,
        apptValue: parseInt(form.apptValue) || 0,
      });
      setStatus("done");
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="deploy" data-testid="lead-section" className="relative mx-auto max-w-7xl scroll-mt-28 px-6 py-24 lg:px-10 lg:py-32">
      <div className="pointer-events-none absolute start-1/3 top-0 h-80 w-80 rounded-full bg-neon-muted blur-[140px]" />
      <div className="grid gap-12 lg:grid-cols-12">
        <Reveal className="lg:col-span-5">
          <p className="overline-tag">{t.nav.deploy}</p>
          <h2 className="mt-4 font-display text-3xl font-extrabold leading-tight md:text-5xl">{t.lead.title}</h2>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-zinc-400 md:text-base">{t.lead.sub}</p>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-7">
          <div className="glass-panel rounded-3xl p-8 md:p-10">
            {status === "done" ? (
              <div className="flex flex-col items-start gap-4 py-10" data-testid="lead-success">
                <CheckCircle2 size={48} className="text-gold" />
                <h3 className="font-display text-2xl font-bold">{t.lead.successTitle}</h3>
                <p className="text-sm text-zinc-400 md:text-base">{t.lead.successBody}</p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="deploy-form" className="grid gap-6 sm:grid-cols-2">
                <div>
                  <label className="mb-2 block text-xs font-bold uppercase tracking-widest text-zinc-500">{t.lead.name}</label>
                  <input required value={form.name} onChange={set("name")} placeholder={t.lead.namePlaceholder} data-testid="f-name" className="field-input" />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-bold uppercase tracking-widest text-zinc-500">{t.lead.whatsapp}</label>
                  <input required type="tel" value={form.whatsapp} onChange={set("whatsapp")} placeholder={t.lead.whatsappPlaceholder} data-testid="f-whatsapp" className="field-input" />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-bold uppercase tracking-widest text-zinc-500">{t.lead.city}</label>
                  <select value={form.city} onChange={set("city")} data-testid="f-city" className="field-input">
                    {CITIES.map((c) => <option key={c} value={c}>{t.roi.cities[c]}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-2 block text-xs font-bold uppercase tracking-widest text-zinc-500">{t.lead.industry}</label>
                  <select value={form.industry} onChange={set("industry")} data-testid="f-industry" className="field-input">
                    {INDUSTRIES.map((i) => <option key={i} value={i}>{t.roi.industries[i]}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-2 block text-xs font-bold uppercase tracking-widest text-zinc-500">{t.lead.missed}</label>
                  <input required type="number" min="0" value={form.missedCalls} onChange={set("missedCalls")} data-testid="f-missed" className="field-input" />
                </div>
                <div>
                  <label className="mb-2 block text-xs font-bold uppercase tracking-widest text-zinc-500">{t.lead.value}</label>
                  <input required type="number" min="0" value={form.apptValue} onChange={set("apptValue")} data-testid="f-value" className="field-input" />
                </div>
                <div className="sm:col-span-2">
                  <button type="submit" disabled={status === "sending"} data-testid="deploy-submit" className="btn-gold w-full disabled:opacity-60">
                    <Send size={16} /> {status === "sending" ? t.lead.sending : t.lead.submit}
                  </button>
                  {status === "error" && <p className="mt-4 text-sm text-red-400" data-testid="lead-error">{t.lead.error}</p>}
                </div>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default LeadCapture;
